"use client";

import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

import styles from "./styles.module.css";
import { getOverlayRoot, getParentZIndex } from "../../../utils/layerManager";

// PopoverContext to share state between components
const PopoverContext = React.createContext({});

// Main Popover component.
// Optionally accepts `open` and `onOpenChange` props for controlled mode.
// When `open` is provided, the popover's visibility is driven externally;
// without it, internal state handles open/close as before.
export function Popover({ children, open: controlledOpen, onOpenChange }) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);

  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (next) => {
    const value = typeof next === "function" ? next(open) : next;
    if (!isControlled) setInternalOpen(value);
    if (typeof onOpenChange === "function") onOpenChange(value);
  };

  // When the popover is open, keep the captured trigger position in sync
  // with the live trigger location. The window-resize listener catches the
  // common "user resizes the browser" case; the ResizeObserver on the
  // trigger's offsetParent catches layout reflows (e.g. sidebar collapse,
  // accordion expand) that don't fire window resize.
  useEffect(() => {
    if (!open || !triggerRef.current) return;
    const recompute = () => {
      const el = triggerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
      });
    };
    window.addEventListener("resize", recompute);
    let observer = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(recompute);
      observer.observe(document.body);
    }
    return () => {
      window.removeEventListener("resize", recompute);
      if (observer) observer.disconnect();
    };
  }, [open]);

  return (
    <PopoverContext.Provider
      value={{ open, setOpen, position, setPosition, triggerRef }}
    >
      {children}
    </PopoverContext.Provider>
  );
}

// Trigger component
export function PopoverTrigger({ children, asChild }) {
  const { open, setOpen, triggerRef, setPosition } =
    React.useContext(PopoverContext);

  const handleClick = (e) => {
    e.stopPropagation();

    // Calculate position for the popover
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
      });
    }

    setOpen(!open);
  };

  // If asChild is true, clone the child and add props
  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children, {
      onClick: handleClick,
      ref: triggerRef,
      className: `${children.props.className || ""} ${styles.trigger}`,
      "aria-expanded": open,
      "aria-haspopup": true,
    });
  }

  // Otherwise, render a button
  return (
    <button
      className={styles.trigger}
      onClick={handleClick}
      ref={triggerRef}
      aria-expanded={open}
      aria-haspopup="true"
    >
      {children}
    </button>
  );
}

// Content component
export function PopoverContent({
  children,
  className,
  align = "center",
  sideOffset = 10,
  zIndexOffset = 10,
  closeOnClickOutside = false,
  closeOnScroll = false,
  disableScroll = false,
  defaultCloseBtn = false,
  ...props
}) {
  const { open, setOpen, position, triggerRef } =
    React.useContext(PopoverContext);
  const contentRef = useRef(null);
  const [zIndex, setZIndex] = useState(null);

  // Handle click outside to close popover
  useEffect(() => {
    if (!open || !closeOnClickOutside) return;

    const handleClickOutside = (event) => {
      if (
        contentRef.current &&
        !contentRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open, setOpen, closeOnClickOutside, triggerRef, contentRef]);

  // Handle scroll to close popover
  useEffect(() => {
    if (!open || !closeOnScroll) return;

    const handleScroll = () => {
      setOpen(false);
    };

    // Listen to scroll events on window and all scrollable parents
    window.addEventListener("scroll", handleScroll, true);
    document.addEventListener("scroll", handleScroll, true);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      document.removeEventListener("scroll", handleScroll, true);
    };
  }, [open, setOpen, closeOnScroll]);

  // Disable scroll when popover is open
  useEffect(() => {
    if (!open || !disableScroll) return;

    // Store the original overflow value
    const originalOverflow = document.body.style.overflow;
    const originalOverflowX = document.body.style.overflowX;
    const originalOverflowY = document.body.style.overflowY;

    // Disable scrolling
    document.body.style.overflow = "hidden";

    return () => {
      // Restore original overflow values
      document.body.style.overflow = originalOverflow;
      document.body.style.overflowX = originalOverflowX;
      document.body.style.overflowY = originalOverflowY;
    };
  }, [open, disableScroll]);

  useEffect(() => {
    if (open && triggerRef?.current) {
      const parentZ = getParentZIndex(triggerRef.current);
      setZIndex(parentZ + zIndexOffset);
    }
  }, [open, triggerRef, zIndexOffset]);

  // Function to calculate and apply alignment
  const calculateAlignment = React.useCallback(() => {
    if (!open || !contentRef.current || !triggerRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const contentRect = contentRef.current.getBoundingClientRect();

    let left = position.left;

    if (align === "start") {
      // Align to start (left)
      left = position.left;
    } else if (align === "center") {
      // Align to center
      left = position.left - contentRect.width / 2 + triggerRect.width / 2;
    } else if (align === "end") {
      // Align to end (right)
      left = position.left - contentRect.width + triggerRect.width;
    }

    // Ensure the popover doesn't go off-screen
    const rightEdge = left + contentRect.width;
    const viewportWidth = window.innerWidth;

    if (rightEdge > viewportWidth) {
      left = viewportWidth - contentRect.width - 8;
    }

    if (left < 8) {
      left = 8;
    }

    contentRef.current.style.left = `${left}px`;
    contentRef.current.style.top = `${position.top + sideOffset}px`;
  }, [open, position, align, sideOffset]);

  // Calculate alignment on initial open and when dependencies change
  useEffect(() => {
    if (open && contentRef.current && triggerRef.current) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        calculateAlignment();
      });
    }
  }, [open, position, align, sideOffset, calculateAlignment]);

  // Observe content size changes and recalculate alignment
  useEffect(() => {
    if (!open || !contentRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      calculateAlignment();
    });

    resizeObserver.observe(contentRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [open, calculateAlignment]);

  const handleClose = (e) => {
    e.stopPropagation();
    setOpen(false);
  };

  if (!open) return null;

  return createPortal(
    <div
      ref={contentRef}
      className={`${styles.content} ${className || ""}`}
      role="dialog"
      aria-modal="true"
      tabIndex={-1}
      style={{
        ...(zIndex !== null && { zIndex }),
      }}
      {...props}
    >
      {defaultCloseBtn && (
        <button
          className={styles.closeButton}
          onClick={handleClose}
          aria-label="Close popover"
          type="button"
        >
          ×
        </button>
      )}

      {children}
    </div>,
    getOverlayRoot()
  );
}
